import React, { useMemo } from 'react';

export interface PremiumBackgroundProps {
  children?: React.ReactNode;
  /**
   * Modo "lite" diminui a quantidade de partículas e simplifica o SVG
   * Ideal para telas com muita carga de processamento na GPU (como NLE)
   */
  performanceMode?: 'lite' | 'full';
  className?: string;
  style?: React.CSSProperties;
}

export const PremiumBackground: React.FC<PremiumBackgroundProps> = ({ 
  children, 
  performanceMode = 'full',
  className = '',
  style = {}
}) => {
  const isLite = performanceMode === 'lite';
  const particleCount = isLite ? 5 : 15;

  const dynamicShapes = useMemo(() => {
    return Array.from({ length: particleCount }).map((_, i) => {
      const size = Math.random() * 65 + 45; // 45 a 110
      const top = isLite ? Math.random() * 80 : 50 + Math.random() * 45; // Em lite, espalha mais. Em full, mais contido no bottom.
      const left = Math.random() * 90;
      const dur = 6 + Math.random() * 4;
      const floatDur = 30 + Math.random() * 20;
      const colors = ['#ffffff', '#a78bfa', '#4ade80', '#facc15', '#60a5fa'];
      const color = colors[i % colors.length];
      const shapes = [
        <circle cx="50" cy="50" r="40" fill={color} />,
        <polygon points="50,10 90,90 10,90" fill={color} />,
        <rect x="20" y="20" width="60" height="60" rx="15" fill={color} transform="rotate(20 50 50)" />
      ];
      
      // Simplifica formas no modo lite
      const shapeElement = isLite ? shapes[0] : shapes[i % 3];

      return (
        <svg 
          key={i} 
          style={{ 
            position: 'absolute', top: `${top}%`, left: `${left}%`, 
            width: size, height: size, opacity: 0, 
            animation: `prm-float1 ${floatDur}s infinite ease-in-out alternate, prm-pulseOpacity ${dur}s infinite ease-in-out ${Math.random() * 5}s` 
          }} 
          viewBox="0 0 100 100"
        >
          {shapeElement}
        </svg>
      );
    });
  }, [particleCount, isLite]);

  return (
    <div 
      className={className}
      style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(-45deg, #0f0a1a, #1a1035, #0d1b2a, #151025)',
        backgroundSize: '400% 400%',
        animation: 'prm-pan-bg 18s ease infinite',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
        ...style
      }}
    >
      {/* Background Shapes for visual richness (Dynamic) */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        {dynamicShapes}
      </div>

      {/* Content wrapper */}
      <div className="flex-1 w-full flex flex-col h-full relative z-[1]">
        {children}
      </div>

      <style>{`
        @keyframes prm-pan-bg {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        @keyframes prm-float1 {
          from { transform: translateY(0) rotate(0deg); }
          to { transform: translateY(-30px) rotate(15deg); }
        }
        @keyframes prm-pulseOpacity {
          0%, 100% { opacity: 0.01; }
          50% { opacity: 0.08; }
        }
      `}</style>
    </div>
  );
};

export default PremiumBackground;
